import React, { useContext, useEffect } from 'react';
import HamQuest from '../../api/HamQuestGiver';
import { QuestRoute } from '../../api/QuestRoutes';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import FormSetting from '../../layout/FormSetting';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Row from '../../layout/Row';
import { userRoles } from '../../RoleProtected';
import { UserContexts } from '../../stateProvider/UsersContext';
import ModalFactory from '../../Modal';
import Checkbox from '../../forms/Checkbox';
import GetState from '../../GetState';
import UpgradeAndAutoSelect from '../../forms/formComponents/UpgradeAndAutoSelect';
import { Features, HasFeature } from '../../RequireFeature';
import { FormContext } from '../../forms/Form';

const deriveWashClubUserMaxUseMode = (hasCustom, value) => {
    if (!hasCustom) return 'UseDefault';
    if (value === null || value === undefined || value === '') return 'Unlimited';
    return Number(value) === 0 ? 'None' : 'Custom';
};

const deriveWashClubUserMaxUseModes = settings => {
    const hasCustom = !!settings.hasCustomMaxUseSettings;
    return {
        ...settings,
        appMaxUsesMode: deriveWashClubUserMaxUseMode(hasCustom, settings.appMaxUses),
        passMaxUsesMode: deriveWashClubUserMaxUseMode(hasCustom, settings.passMaxUses),
        maxRedemptionsMode: deriveWashClubUserMaxUseMode(hasCustom, settings.maxRedemptions),
        tellerReuseLimitMode: deriveWashClubUserMaxUseMode(hasCustom, settings.tellerReuseLimit),
        timerMaxUsesMode: deriveWashClubUserMaxUseMode(hasCustom, settings.timerMaxUses),
        timerReuseLimitMode: deriveWashClubUserMaxUseMode(hasCustom, settings.timerReuseLimit),
    };
};

const WCUserSettingsForm = () => {
    const { values, setValues, editing } = useContext(FormContext);
    const hasNewMaxUses = HasFeature(Features.NewMaxUses);
    const maxUseModeOptions = HSOptions.washClub.maxUseMode;
    const isDefinitelySet = mode => mode === 'Custom' || mode === 'None';
    const showMaxRedemptions = !isDefinitelySet(values.appMaxUsesMode) || !isDefinitelySet(values.passMaxUsesMode);

    useEffect(() => {
        if (values.maxRedemptionsMode !== 'Custom') return;
        const app = values.appMaxUsesMode === 'Custom' && values.appMaxUses !== '' && values.appMaxUses != null ? Number(values.appMaxUses) : null;
        const pass = values.passMaxUsesMode === 'Custom' && values.passMaxUses !== '' && values.passMaxUses != null ? Number(values.passMaxUses) : null;
        if (app !== null && pass !== null && !isNaN(app) && !isNaN(pass)) {
            setValues(v => ({ ...v, maxRedemptions: String(app + pass) }));
        }
    }, [values.appMaxUses, values.passMaxUses, values.appMaxUsesMode, values.passMaxUsesMode, values.maxRedemptionsMode]);

    return (
        <>
            {hasNewMaxUses ? (
                <>
                    <Row wrap>
                        <Select
                            label='App Monthly Uses Mode'
                            name='appMaxUsesMode'
                            options={maxUseModeOptions}
                            required
                        />
                        {values.appMaxUsesMode === 'Custom' ? (
                            <Input
                                label='App Monthly Uses'
                                name='appMaxUses'
                                type='number'
                                min={1}
                                disabled={editing}
                                required
                                tooltipText='Maximum App redemptions that can be vended in a given billing period.'
                            />
                        ) : (
                            <Input
                                label='App Monthly Uses'
                                value={values.appMaxUsesMode === 'None' ? '0' : ''}
                                disable
                            />
                        )}
                    </Row>
                    <Row wrap>
                        <Select
                            label='Pass Monthly Uses Mode'
                            name='passMaxUsesMode'
                            options={maxUseModeOptions}
                            required
                        />
                        {values.passMaxUsesMode === 'Custom' ? (
                            <Input
                                label='Pass Monthly Uses'
                                name='passMaxUses'
                                type='number'
                                min={1}
                                disabled={editing}
                                required
                                tooltipText='Maximum Pass redemptions that can be vended in a given billing period.'
                            />
                        ) : (
                            <Input
                                label='Pass Monthly Uses'
                                value={values.passMaxUsesMode === 'None' ? '0' : ''}
                                disable
                            />
                        )}
                    </Row>
                    {showMaxRedemptions && (
                        <Row wrap>
                            <Select
                                label='Total Monthly Redemptions Mode'
                                name='maxRedemptionsMode'
                                options={maxUseModeOptions}
                                required
                            />
                            {values.maxRedemptionsMode === 'Custom' ? (
                                <Input
                                    label='Total Monthly Redemptions'
                                    name='maxRedemptions'
                                    type='number'
                                    min={1}
                                    disabled={editing}
                                    required
                                    tooltipText='Maximum total redemptions (Pass + App) per billing period. Only used when App Monthly Uses or Pass Monthly Uses is not Custom or None.'
                                />
                            ) : (
                                <Input
                                    label='Total Monthly Redemptions'
                                    value={values.maxRedemptionsMode === 'None' ? '0' : ''}
                                    disable
                                />
                            )}
                        </Row>
                    )}
                    <Row wrap>
                        <Select
                            label='Kiosk Daily Use Limit Mode'
                            name='tellerReuseLimitMode'
                            options={maxUseModeOptions}
                            required
                        />
                        {values.tellerReuseLimitMode === 'Custom' ? (
                            <Input
                                label='Kiosk Daily Use Limit'
                                name='tellerReuseLimit'
                                type='number'
                                min={1}
                                disabled={editing}
                                required
                                tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the teller.'
                            />
                        ) : (
                            <Input
                                label='Kiosk Daily Use Limit'
                                value={values.tellerReuseLimitMode === 'None' ? '0' : ''}
                                disable
                            />
                        )}
                    </Row>
                    <Row wrap>
                        <Select
                            label='Timer Monthly Uses Mode'
                            name='timerMaxUsesMode'
                            options={maxUseModeOptions}
                            required
                        />
                        {values.timerMaxUsesMode === 'Custom' ? (
                            <Input
                                label='Timer Monthly Uses'
                                name='timerMaxUses'
                                type='number'
                                min={1}
                                disabled={editing}
                                required
                            />
                        ) : (
                            <Input
                                label='Timer Monthly Uses'
                                value={values.timerMaxUsesMode === 'None' ? '0' : ''}
                                disable
                            />
                        )}
                    </Row>
                    <Row wrap>
                        <Select
                            label='Timer Daily Use Limit Mode'
                            name='timerReuseLimitMode'
                            options={maxUseModeOptions}
                            required
                        />
                        {values.timerReuseLimitMode === 'Custom' ? (
                            <Input
                                label='Timer Daily Use Limit'
                                name='timerReuseLimit'
                                type='number'
                                min={1}
                                disabled={editing}
                                required
                                tooltipText='Maximum number of times in one Calendar Day that a customer can redeem at the timers.'
                            />
                        ) : (
                            <Input
                                label='Timer Daily Use Limit'
                                value={values.timerReuseLimitMode === 'None' ? '0' : ''}
                                disable
                            />
                        )}
                    </Row>
                </>
            ) : (
                <>
                    <Row wrap>
                        <Select
                            label='Max Use Period'
                            name='passMaxUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault') ? (
                            <Input
                                label='Pass Max Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='Pass Max Uses'
                                name='passMaxUses'
                                type='number'
                                min={1}
                                disabled={values.passMaxUsePeriod === 'Unlimited' || values.passMaxUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row wrap>
                        <Select
                            label='App Scan Max Use Period'
                            name='appIdPassMaxUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.appIdPassMaxUsePeriod === 'Unlimited' || values.appIdPassMaxUsePeriod === 'UseDefault') ? (
                            <Input
                                label='App Scan Max Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='App Scan Max Uses'
                                name='appIdPassMaxUses'
                                type='number'
                                min={1}
                                disabled={values.appIdPassMaxUsePeriod === 'Unlimited' || values.appIdPassMaxUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row wrap>
                        <Select
                            label='App Scan Max Timer Use Period'
                            name='appIdPassMaxTimerUsePeriod'
                            options={HSOptions.washClub.period}
                            required
                        />
                        {(values.appIdPassMaxTimerUsePeriod === 'Unlimited' || values.appIdPassMaxTimerUsePeriod === 'UseDefault') ? (
                            <Input
                                label='App Scan Max Timer Uses'
                                disabled
                                required
                            />
                        ) : (
                            <Input
                                label='App Scan Max Timer Uses'
                                name='appIdPassMaxTimerUses'
                                type='number'
                                min={1}
                                disabled={values.appIdPassMaxTimerUsePeriod === 'Unlimited' || values.appIdPassMaxTimerUsePeriod === 'UseDefault' || editing}
                                required
                            />
                        )}
                    </Row>

                    <Row align='flex-end' wrap>
                        <Checkbox
                            label='Use Default Pass Reuse Time'
                            name='useDefaultPassReuseTime'
                        />
                        {values.useDefaultPassReuseTime ?
                            <Input
                                label='Pass Reuse Inhibit Time (min)'
                                disabled
                                required
                            /> :
                            <Input
                                label='Pass Reuse Inhibit Time (min)'
                                name='passReuseInhibitTime'
                                type='number'
                                disabled={values.useDefaultPassReuseTime || editing}
                                required
                            />
                        }
                    </Row>
                </>
            )}

            <UpgradeAndAutoSelect autoSelectFieldName='autoSelect' />

            <Row align='flex-end' wrap>
                <Select
                    label='Kiosk Receipt Mode'
                    name='receiptMode'
                    options={HSOptions.washClub.receiptMode}
                    required
                />
            </Row>
        </>
    );
};

const WashClubSettings = ({ ...props }) => {
    const context = useContext(UserContexts);

    const [usersWCSettings, refreshWCSettings] = GetState(QuestRoute.user.washClubSettings(context.user.customerId));

    const resetUserWCSettings = (user) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: (<span>Are you sure you want to restore Wash Club default settings for {context.user.firstName}?</span>),
                type: 'warning',
            },
            promise: {
                title: 'Restoring Defaults',
                body: `Restoring, please wait.`,
                callback: () => HamQuest.delete(QuestRoute.user.washClubSettings(context.user.customerId))
            },
            confirmed: {
                title: 'Success',
                body: `${context.user.firstName}'s settings have been set to default.`,
                type: 'success'
            },
            finally: () => refreshWCSettings()
        })
    }

    return (
        <FormSetting
            name='washClubUserSettings'
            label={`User's Wash Club Settings`}
            onSubmit={e => {
                if (e.allowUpgrades == true || e.allowUpgrades == false)
                    e.allowUpgrades = e.allowUpgrades.toString().charAt(0).toUpperCase() + e.allowUpgrades.toString().slice(1).toLowerCase();
                if (e.autoSelect == true || e.autoSelect == false)
                    e.autoSelect = e.autoSelect.toString().charAt(0).toUpperCase() + e.autoSelect.toString().slice(1).toLowerCase();
                return HamQuest.put(QuestRoute.user.washClubSettings(context.user.customerId), { ...e }).then(() => refreshWCSettings())
            }}
            inputs={deriveWashClubUserMaxUseModes({ ...usersWCSettings })}
            controls={[<Button label='Restore Default Settings' onClick={resetUserWCSettings} />]}
            roles={userRoles.editCustomers}
            text='Update'
            editable
        >
            {() => <WCUserSettingsForm />}
        </FormSetting>
    );
}

export default WashClubSettings;
